import React, { useState, useEffect, useCallback, useRef } from 'react';
import opensearchService from '../services/opensearchService';
import './OpenSearchAdmin.css';

const LOG_COLS = ['@timestamp', 'logGroup', 'logStream', 'message'];
const LOG_SIZE = 50;

const fmt = (n) => n != null ? Number(n).toLocaleString() : '-';
const truncate = (str, n = 120) => str && str.length > n ? str.slice(0, n) + '…' : (str || '');

// A CloudWatch/Lambda log `message` is many lines joined by newlines, each line usually
// "<iso-timestamp>\t<requestId>\t<LEVEL>\t<text>". Render it as readable lines: strip the requestId,
// keep a short time + level + text, and drop the repeated boilerplate so it's not a wall of escapes.
const formatLogMessage = (msg) => {
    if (typeof msg !== 'string') return String(msg == null ? '' : msg);
    const normalized = msg.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    return normalized
        .split('\n')
        .map((line) => {
            const parts = line.split('\t');
            if (parts.length >= 4 && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(parts[0])) {
                const time = parts[0].slice(11, 23);          // HH:MM:SS.mmm
                const level = (parts[2] || '').padEnd(5);
                return `${time}  ${level}  ${parts.slice(3).join('\t')}`;
            }
            return line;
        })
        .join('\n')
        .replace(/\n{3,}/g, '\n\n');                          // collapse runs of blank lines
};

// Count occurrences of "error" (any case) in a log message.
const countErrors = (text) => (String(text == null ? '' : text).match(/error/gi) || []).length;

// Wrap every occurrence of "error" (any case) in a highlight span so failures jump out in the log view.
const highlightErrors = (text) => {
    const parts = String(text == null ? '' : text).split(/(error)/gi);
    return parts.map((part, i) =>
        /^error$/i.test(part)
            ? <span key={i} style={{ background: '#fee2e2', color: '#b91c1c', fontWeight: 700, borderRadius: 2, padding: '0 1px' }}>{part}</span>
            : part
    );
};

const TIME_RANGE_OPTIONS = [
    { label: 'Last 1h',  hours: 1 },
    { label: 'Last 6h',  hours: 6 },
    { label: 'Last 24h', hours: 24 },
    { label: 'Last 7d',  hours: 168 },
    { label: 'Last 30d', hours: 720 },
    { label: 'All time', hours: 0 },
];

export default function LogSearch({ defaultPattern, lockPattern = false, title = 'Log Search', defaultHours = 0 }) {
    // Allow deep-linking a pre-filled search via ?q=... (e.g. the reconciliation table's "view logs"
    // buttons open this page with the source/target record id already in the query box).
    const initialQ = (() => {
        try { return new URLSearchParams(window.location.search).get('q') || ''; } catch { return ''; }
    })();

    const [inputPattern, setInputPattern] = useState(defaultPattern);
    const [activePattern, setActivePattern] = useState(defaultPattern);
    const [inputLogQ, setInputLogQ] = useState(initialQ);
    const [logQ, setLogQ] = useState(initialQ);
    const [logDocs, setLogDocs] = useState([]);
    const [logTotal, setLogTotal] = useState(0);
    const [logFrom, setLogFrom] = useState(0);
    const [logLoading, setLogLoading] = useState(false);
    const [logError, setLogError] = useState(null);
    const [logExpanded, setLogExpanded] = useState(null);
    const [hours, setHours] = useState(defaultHours);
    const debounceLogQ = useRef(null);

    const [logGroupOptions, setLogGroupOptions] = useState([]);
    const [logGroupFilter, setLogGroupFilter] = useState('');
    const [logGroupInput, setLogGroupInput] = useState('');
    const [logGroupOpen, setLogGroupOpen] = useState(false);
    const logGroupRef = useRef(null);

    const [logStreamOptions, setLogStreamOptions] = useState([]);
    const [logStreamFilter, setLogStreamFilter] = useState('');
    const [logStreamInput, setLogStreamInput] = useState('');
    const [logStreamOpen, setLogStreamOpen] = useState(false);
    const logStreamRef = useRef(null);

    const searchLogs = useCallback(async (pattern, q, from, logStream, logGroup, h) => {
        if (!pattern) return;
        setLogLoading(true);
        setLogError(null);
        try {
            const res = await opensearchService.searchDocs(pattern, {
                q, size: LOG_SIZE, from,
                logStream: logStream || undefined,
                logGroup: logGroup || undefined,
                hours: h > 0 ? h : undefined,
            });
            setLogDocs(res.data.hits || []);
            setLogTotal(res.data.total || 0);
        } catch (e) {
            setLogError(e.response?.data?.message || e.message || 'Search failed');
            setLogDocs([]);
            setLogTotal(0);
        } finally {
            setLogLoading(false);
        }
    }, []);

    const loadLogGroupOptions = useCallback(async (pattern) => {
        if (!pattern) return;
        try {
            const res = await opensearchService.getAggs(pattern, 'logGroup.keyword');
            setLogGroupOptions(res.data.values || []);
        } catch {
            setLogGroupOptions([]);
        }
    }, []);

    const loadLogStreamOptions = useCallback(async (pattern) => {
        if (!pattern) return;
        try {
            const res = await opensearchService.getAggs(pattern, 'logStream.keyword');
            setLogStreamOptions(res.data.values || []);
        } catch {
            setLogStreamOptions([]);
        }
    }, []);

    useEffect(() => {
        searchLogs(activePattern, logQ, logFrom, logStreamFilter, logGroupFilter, hours);
    }, [searchLogs, activePattern, logQ, logFrom, logStreamFilter, logGroupFilter, hours]);

    useEffect(() => {
        loadLogGroupOptions(activePattern);
        loadLogStreamOptions(activePattern);
        setLogGroupFilter('');
        setLogGroupInput('');
        setLogStreamFilter('');
        setLogStreamInput('');
    }, [loadLogGroupOptions, loadLogStreamOptions, activePattern]);

    useEffect(() => {
        const handler = (e) => {
            if (logGroupRef.current && !logGroupRef.current.contains(e.target)) setLogGroupOpen(false);
            if (logStreamRef.current && !logStreamRef.current.contains(e.target)) setLogStreamOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogQ = (val) => {
        setInputLogQ(val);
        clearTimeout(debounceLogQ.current);
        debounceLogQ.current = setTimeout(() => { setLogFrom(0); setLogQ(val); }, 400);
    };

    // Explicit search (button / Enter) — apply the typed query immediately, cancelling the debounce.
    const runLogSearch = () => {
        clearTimeout(debounceLogQ.current);
        setLogFrom(0);
        setLogQ(inputLogQ);
    };

    const applyPattern = () => {
        setActivePattern(inputPattern.trim() || defaultPattern);
        setLogFrom(0);
        setLogGroupFilter('');
        setLogGroupInput('');
        setLogStreamFilter('');
        setLogStreamInput('');
    };

    const selectLogGroup = (val) => { setLogGroupFilter(val); setLogGroupInput(val); setLogGroupOpen(false); setLogFrom(0); };
    const clearLogGroup  = ()    => { setLogGroupFilter('');  setLogGroupInput('');  setLogFrom(0); };
    const selectLogStream = (val) => { setLogStreamFilter(val); setLogStreamInput(val); setLogStreamOpen(false); setLogFrom(0); };
    const clearLogStream  = ()    => { setLogStreamFilter('');  setLogStreamInput('');  setLogFrom(0); };

    const filteredGroupOptions  = logGroupOptions.filter(o => !logGroupInput  || o.toLowerCase().includes(logGroupInput.toLowerCase()));
    const filteredStreamOptions = logStreamOptions.filter(o => !logStreamInput || o.toLowerCase().includes(logStreamInput.toLowerCase()));

    const logPages = Math.ceil(logTotal / LOG_SIZE);
    const logPage  = Math.floor(logFrom / LOG_SIZE);
    const hasClearable = logQ || logGroupFilter || logStreamFilter || hours !== defaultHours || (!lockPattern && activePattern !== defaultPattern);

    return (
        <div className="acp-page" style={{ maxWidth: 1200 }}>
            <div className="acp-header">
                <h1 className="acp-title">{title}</h1>
            </div>

            <div className="acp-card">
                <div className="acp-card-header">
                    <span className="acp-card-title">Log Search</span>
                    {logLoading && <span className="os-loading-dot">Searching…</span>}
                    {!logLoading && logTotal > 0 && <span className="os-doc-count">{fmt(logTotal)} documents</span>}
                </div>

                <div className="os-log-search-bar">
                    {!lockPattern && (
                        <div className="os-log-pattern-wrap">
                            <label className="os-filter-label">Index pattern</label>
                            <div className="os-pattern-input-row">
                                <input
                                    className="acp-input os-pattern-input"
                                    value={inputPattern}
                                    onChange={e => setInputPattern(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && applyPattern()}
                                    placeholder={`e.g. ${defaultPattern}`}
                                    spellCheck={false}
                                />
                            </div>
                        </div>
                    )}

                    <div className="os-log-stream-wrap" ref={logGroupRef}>
                        <label className="os-filter-label">Log group</label>
                        <div className="os-stream-dropdown">
                            <div className="os-stream-input-row">
                                <input
                                    className="acp-input os-stream-input"
                                    value={logGroupInput}
                                    onChange={e => { setLogGroupInput(e.target.value); setLogGroupOpen(true); if (!e.target.value) clearLogGroup(); }}
                                    onFocus={() => setLogGroupOpen(true)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && filteredGroupOptions.length > 0) selectLogGroup(filteredGroupOptions[0]);
                                        if (e.key === 'Escape') setLogGroupOpen(false);
                                    }}
                                    placeholder="All log groups…"
                                />
                                {logGroupFilter && <button className="os-stream-clear-x" onClick={clearLogGroup} title="Clear">✕</button>}
                            </div>
                            {logGroupOpen && filteredGroupOptions.length > 0 && (
                                <ul className="os-stream-list">
                                    {filteredGroupOptions.slice(0, 50).map(o => (
                                        <li key={o} className={`os-stream-option${logGroupFilter === o ? ' selected' : ''}`} onMouseDown={() => selectLogGroup(o)}>{o}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="os-log-stream-wrap" ref={logStreamRef}>
                        <label className="os-filter-label">Log stream</label>
                        <div className="os-stream-dropdown">
                            <div className="os-stream-input-row">
                                <input
                                    className="acp-input os-stream-input"
                                    value={logStreamInput}
                                    onChange={e => { setLogStreamInput(e.target.value); setLogStreamOpen(true); if (!e.target.value) clearLogStream(); }}
                                    onFocus={() => setLogStreamOpen(true)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && filteredStreamOptions.length > 0) selectLogStream(filteredStreamOptions[0]);
                                        if (e.key === 'Escape') setLogStreamOpen(false);
                                    }}
                                    placeholder="All log streams…"
                                />
                                {logStreamFilter && <button className="os-stream-clear-x" onClick={clearLogStream} title="Clear">✕</button>}
                            </div>
                            {logStreamOpen && filteredStreamOptions.length > 0 && (
                                <ul className="os-stream-list">
                                    {filteredStreamOptions.slice(0, 50).map(o => (
                                        <li key={o} className={`os-stream-option${logStreamFilter === o ? ' selected' : ''}`} onMouseDown={() => selectLogStream(o)}>{o}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <div className="os-log-q-wrap">
                        <label className="os-filter-label">Search</label>
                        <input
                            className="acp-input os-log-q-input"
                            value={inputLogQ}
                            onChange={e => handleLogQ(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') runLogSearch(); }}
                            placeholder="Search messages…"
                        />
                    </div>

                    <div className="os-log-q-wrap" style={{ justifyContent: 'flex-end' }}>
                        <label className="os-filter-label">&nbsp;</label>
                        <button className="acp-btn acp-btn-primary" onClick={runLogSearch} disabled={logLoading}>
                            {logLoading ? 'Searching…' : 'Search'}
                        </button>
                    </div>

                    <div className="os-log-q-wrap">
                        <label className="os-filter-label">Time range</label>
                        <select
                            className="acp-input os-log-q-input"
                            value={hours}
                            onChange={e => { setHours(parseInt(e.target.value, 10)); setLogFrom(0); }}
                        >
                            {TIME_RANGE_OPTIONS.map(o => (
                                <option key={o.hours} value={o.hours}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {hasClearable && (
                        <button className="acp-btn acp-btn-secondary acp-btn-sm os-log-clear-btn" onClick={() => {
                            setInputLogQ(''); setLogQ('');
                            if (!lockPattern) { setInputPattern(defaultPattern); setActivePattern(defaultPattern); }
                            setLogGroupFilter(''); setLogGroupInput('');
                            setLogStreamFilter(''); setLogStreamInput('');
                            setHours(defaultHours);
                            setLogFrom(0);
                        }}>
                            Clear all
                        </button>
                    )}
                </div>

                {logError && <div className="os-msg os-msg-error" style={{ margin: '0.75rem 1.25rem' }}>{logError}</div>}

                {logDocs.length === 0 && !logLoading ? (
                    <div className="acp-empty">{logQ ? 'No results' : 'No documents found'}</div>
                ) : (
                    <>
                        <div className="os-doc-table-wrap">
                            <table className="acp-table os-log-table">
                                <thead>
                                    <tr>
                                        {LOG_COLS.map(c => <th key={c}>{c}</th>)}
                                        <th style={{ textAlign: 'center' }}>Errors</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logDocs.map((d) => {
                                        const s = d.source || {};
                                        const isExp = logExpanded === d.id;
                                        return (
                                            <React.Fragment key={d.id}>
                                                <tr
                                                    className={isExp ? 'os-doc-row-expanded' : ''}
                                                    onClick={() => setLogExpanded(isExp ? null : d.id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="os-log-ts">{s['@timestamp'] ? new Date(s['@timestamp']).toLocaleString() : ''}</td>
                                                    <td className="os-log-group">{s.logGroup || ''}</td>
                                                    <td className="os-log-stream">{s.logStream || ''}</td>
                                                    <td className="os-log-msg">{truncate(s.message)}</td>
                                                    {(() => { const ec = countErrors(s.message); return (
                                                        <td style={{ textAlign: 'center', fontWeight: 700, color: ec > 0 ? '#b91c1c' : '#94a3b8' }}>{ec}</td>
                                                    ); })()}
                                                    <td><span className="os-expand-icon">{isExp ? '▲' : '▼'}</span></td>
                                                </tr>
                                                {isExp && (
                                                    <tr className="os-doc-json-row">
                                                        <td colSpan={LOG_COLS.length + 2}>
                                                            {s.message ? (
                                                                <pre className="os-doc-json" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{highlightErrors(formatLogMessage(s.message))}</pre>
                                                            ) : (
                                                                <pre className="os-doc-json">{JSON.stringify(s, null, 2)}</pre>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {logPages > 1 && (
                            <div className="os-pagination">
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage === 0} onClick={() => setLogFrom(0)}>«</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage === 0} onClick={() => setLogFrom(Math.max(0, logFrom - LOG_SIZE))}>‹</button>
                                <span className="os-page-info">Page {logPage + 1} of {logPages}</span>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage >= logPages - 1} onClick={() => setLogFrom(logFrom + LOG_SIZE)}>›</button>
                                <button className="acp-btn acp-btn-secondary acp-btn-sm" disabled={logPage >= logPages - 1} onClick={() => setLogFrom((logPages - 1) * LOG_SIZE)}>»</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
